import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Header } from "../components/Header";

const Section = ({ title, children }) => (
  <section className="space-y-1.5">
    <h2 className="text-sm font-semibold tracking-tight text-white">{title}</h2>
    <div className="text-xs leading-relaxed text-[#A1A1AA] space-y-2">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap">Legal</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1">Privacy Policy</h1>
            <p className="text-[11px] font-mono text-[#71717A] mt-1">
              Effective date: 2026-02-19 · Last updated: 2026-02-19
            </p>
          </div>
          <Link to="/" data-testid="privacy-back-btn" className="btn-outline flex items-center gap-1.5">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>

        <div className="border border-[#FF9F0A] bg-[rgba(255,159,10,0.06)] p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-[#FF9F0A] mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-[#FFE5B4] leading-relaxed">
            <strong className="text-white">PLACEHOLDER — REVIEW BY QUALIFIED COUNSEL BEFORE PRODUCTION USE.</strong>{" "}
            Tailor this document to applicable privacy laws (GDPR, CCPA, etc.) for your jurisdictions of operation.
          </p>
        </div>

        <div className="panel p-5 space-y-5">
          <Section title="1. What We Collect">
            <p>When you sign in with Google, we receive and store:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Google account identifier, email address, name, and profile picture</li>
              <li>The positions, tickers, and watchlists you save</li>
              <li>Your roll history (each candidate you analyze and the rolls you log)</li>
              <li>Your subscription status and Stripe customer / subscription identifiers</li>
              <li>Any messages you submit via the in-app Contact form</li>
              <li>A timestamped audit log of key actions (login, position create, position delete, ticker load, contact submission, broker-disclaimer acceptance, billing events)</li>
              <li>IP address and user-agent string captured at sign-in and at broker-disclaimer acceptance, for fraud prevention and audit</li>
            </ul>
          </Section>

          <Section title="2. What We Do Not Collect">
            ROLL.OPS does <strong className="text-white">not</strong> request, receive, or store your broker credentials,
            brokerage account numbers, holdings outside of what you manually enter, banking
            information, full payment card data (handled by Stripe), social security numbers, or
            government identification documents.
          </Section>

          <Section title="3. How We Use Your Data">
            <ul className="list-disc pl-5 space-y-1">
              <li>To authenticate you and operate the Service</li>
              <li>To compute and display your personal positions, watchlists, and roll history</li>
              <li>To process subscription payments and renewals via Stripe</li>
              <li>To respond to your contact-form messages</li>
              <li>To detect abuse, audit administrative actions, and improve product quality</li>
              <li>To send transactional emails (subscription receipts, contact-form acknowledgments)</li>
            </ul>
          </Section>

          <Section title="4. Third Parties">
            We share limited data with the following processors strictly to operate the Service:
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-white">Google</strong> (sign-in identity provider)</li>
              <li><strong className="text-white">Stripe, Inc.</strong> (subscription payments and customer billing portal)</li>
              <li><strong className="text-white">Gmail / Google Workspace</strong> (transactional email delivery)</li>
              <li><strong className="text-white">Tradier, Inc.</strong> (market data — quotes and option chains; no user data is sent here)</li>
              <li><strong className="text-white">MongoDB</strong> (data storage at our hosting provider)</li>
              <li><strong className="text-white">Emergent</strong> (application hosting / deployment platform)</li>
            </ul>
            We do <strong className="text-white">not</strong> sell your data to third parties, and we do not share data with advertisers
            or data brokers.
          </Section>

          <Section title="5. Cookies & Session Tracking">
            We use a single HTTP-only session cookie to keep you signed in. We do not use
            third-party analytics cookies, advertising trackers, or fingerprinting.
          </Section>

          <Section title="6. Data Retention">
            We retain your data as long as your account is active. When you delete your account
            (or when an admin deletes it), all personally identifying records are removed.
            Anonymized audit-log entries may be retained for compliance and security purposes.
          </Section>

          <Section title="7. Your Rights">
            Depending on your jurisdiction (e.g., GDPR in the EU, CCPA in California), you may
            have rights to access, correct, export, or delete your personal data. Email or use the
            in-app Contact form to make a request. We will respond within applicable statutory
            timeframes.
          </Section>

          <Section title="8. Security">
            Data in transit is encrypted with TLS. Sensitive credentials (Stripe API keys, Gmail
            App Password) are stored as encrypted environment variables on our hosting platform.
            However, no system is perfectly secure — please use a unique strong password on your
            Google account and enable two-factor authentication.
          </Section>

          <Section title="9. Children">
            ROLL.OPS is not directed to anyone under 18. We do not knowingly collect data from
            minors. If you believe a minor has provided us data, contact us and we will delete it.
          </Section>

          <Section title="10. Changes">
            We may update this Privacy Policy from time to time. Material changes will be
            highlighted in the app or by email to your account address.
          </Section>

          <Section title="11. Contact">
            For privacy questions, requests, or complaints, use the in-app Contact form (Help menu
            → Contact us).
          </Section>

          <div className="text-[10px] text-[#71717A] border-t border-[#27272A] pt-3 leading-relaxed">
            This Privacy Policy is a non-binding template provided as a starting point. Specific
            data-protection regulations (GDPR, CCPA, LGPD, etc.) impose substantive obligations
            that must be reflected here by qualified counsel before relying on this document.
          </div>
        </div>
      </main>
    </div>
  );
}
